import React, { use, useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import {
  AuditLogDTO,
  EndorsementLicenceTypeDTO,
  EndorsementMotivationDTO,
  EndorsementStatusDTO,
  MemberDTO,
  MemberEndorsementDTO,
  MemberFirearmDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { getMemberId, getRoles } from '../../utils/tokenUtils';
import { FirearmMotivationTypeConstants } from '../../constants/FirearmMotivationTypeConstants';
import ImageLink from '../common/ImageLink';
import FileUpload from '../common/FileUpload';
import { useFileApi } from '../../hooks/useFileApi';
import { useEndorsementMotivations } from '../../hooks/useEndorsementMotivationApi';
import FirearmFieldsForm from './FirearmFieldsForm';
import { useMemberFirearmApi } from '../../hooks/useMemberFirearmApi';
import { useEndorsementLicenceTypes } from '../../hooks/useEndorsementLicenceTypeApi';
import { EndorsementLicenceTypeConstants } from '../../constants/EndorsementLicenceTypeConstants';
import { FirearmSerialTypeConstants } from '../../constants/FirearmSerialTypeConstants';
import { useNavigate } from 'react-router-dom';
import { EndorsementFirearmRenewalConstants } from '../../constants/EndorsementFirearmRenewalConstants';
import { useEndorsementStatuses } from '../../hooks/useEndorsementStatusApi';
import AuditLogTable from '../tables/AuditLogTable';
import { useAuditLogApi } from '../../hooks/useAuditLogApi';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import FeedbackForm from './FeedbackForm';
import { ComponentConstants } from '../../constants/ComponentConstants';
import MemberDetails from '../common/MemberDetails';
import { FileTypeConstants } from '../../constants/FileTypeConstants';
import { buildFirearmOptions } from '../../utils/formUtils/buildFirearmOptions';
import { buildFellowOptions } from '../../utils/formUtils/buildFellowOptions';
import { confirmAction } from '../../utils/sweetAlertUtils';
import { useFellows } from '../../hooks/useFellowApi';
import { FellowConstants } from '../../constants/FellowConstants';
import { useMemberApi } from '../../hooks/useMemberApi';
import { useMemberEndorsementApi } from '../../hooks/useMemberEndorsementApi';

interface MemberEndorsementFormProps {
  initialValues: MemberEndorsementDTO;
  onSubmit: (data: MemberEndorsementDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface SaveFireArm {
  saveFireArm: boolean;
}

const MemberEndorsementForm: React.FC<MemberEndorsementFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<MemberEndorsementDTO>(initialValues);
  const [member, setMember] = useState<MemberDTO>();
  const [saveFireArm, setSaveFireArm] = useState<SaveFireArm>({
    saveFireArm: false,
  });
  const { getFeedback } = useMemberEndorsementApi();

  // Feedback HTML is stored in blob storage now, not returned inline with the MemberEndorsement DTO — fetch it on demand.
  useEffect(() => {
    if (!formData.id) return;
    getFeedback(formData.id)
      .then((feedback) => setFormData((prev) => ({ ...prev, feedback })))
      .catch(() => { });
  }, [formData.id]);
  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});
  const { uploadFile, getFileUrl } = useFileApi();
  const navigate = useNavigate();
  const memberId = getMemberId();
  const [auditLogs, setAuditLogs] = useState<AuditLogDTO[]>([]);
  const [auditCurrentPage, setAuditCurrentPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(10);

  const statusFieldName = 'statusId';

  const roles = getRoles() || [];
  const FORM_SECTIONS = {
    ...(roles?.includes('Admin')
      ? {
        memberDetails: {
          id: 'member-details',
          title: 'Member Details',
        },
      }
      : {}),
    firearmDetails: {
      id: 'firearm-details',
      title: 'Firearm Details',
    },
    endorsementDetails: {
      id: 'endorsement-details',
      title: 'Endorsement Details',
    },
    feedback: {
      id: 'feedback',
      title: 'Feedback',
    },
    ...(roles?.includes('Admin')
      ? {
        history: {
          id: 'history',
          title: 'History',
        },
      }
      : {}),
  } as const;

  // Convert MemberEndorsementDTO setter to MemberFirearmDTO setter
  const setFirearmData = (
    firearmData:
      | MemberFirearmDTO
      | ((prev: MemberFirearmDTO) => MemberFirearmDTO),
  ) => {
    setFormData((prevEndorsement) => ({
      ...prevEndorsement,
      firearm:
        typeof firearmData === 'function'
          ? firearmData(prevEndorsement.firearm || {})
          : firearmData,
    }));
  };

  const [licenceTypes, setLicenceTypes] = useState<EndorsementLicenceTypeDTO[]>(
    [],
  );
  const { getById: getMemberById } = useMemberApi();
  const [memberFirearms, setMemberFirearms] = useState<MemberFirearmDTO[]>([]);
  const [motivationTypes, setMotivationTypes] = useState<EndorsementMotivationDTO[]>([]);
  const { data: fellows, isLoading: isLoadingFellows } = useFellows({ canWriteEndorsementMotivations: true });
  const [statuses, setStatuses] = useState<EndorsementStatusDTO[]>([]);

  const { data: licenceTypesData, isLoading: isLoadingLicenceTypes } = useEndorsementLicenceTypes();
  const { search: searchMemberFirearms, isLoading: isLoadingMemberFirearms } = useMemberFirearmApi();
  const { data: motivationTypesData, isLoading: isLoadingMotivationTypes } = useEndorsementMotivations();
  const { data: statusesData, isLoading: isLoadingStatuses } = useEndorsementStatuses();
  const { search: searchAuditLogs, isLoading: isLoadingAuditLogs } = useAuditLogApi();

  const validationRules: ValidationRules = {
    licenceTypeId: [
      { required: true, message: 'Application Type is required' },
    ],
    memberFirearmId: [
      {
        required:
          formData.licenceTypeId ===
          EndorsementLicenceTypeConstants.RENEWAL_OF_EXISTING_LICENCE,
        message: 'Firearm is required',
      },
    ],
    firearm: {
      firearmTypeId: [
        {
          required:
            formData.licenceTypeId ===
            EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM ||
            formData.memberFirearmId ===
            EndorsementFirearmRenewalConstants.NEW_FIREARM,
          message: 'Firearm Type is required',
        },
      ],
      firearmMakeId: [
        {
          required:
            formData.licenceTypeId ===
            EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM ||
            formData.memberFirearmId ===
            EndorsementFirearmRenewalConstants.NEW_FIREARM,
          message: 'Firearm Make is required',
        },
      ],
      firearmSpecificId: [
        {
          required:
            formData.licenceTypeId ===
            EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM ||
            formData.memberFirearmId ===
            EndorsementFirearmRenewalConstants.NEW_FIREARM,
          message: 'Firearm Specific is required',
        },
      ],
      firearmCaliberId: [
        {
          required:
            formData.licenceTypeId ===
            EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM ||
            formData.memberFirearmId ===
            EndorsementFirearmRenewalConstants.NEW_FIREARM,
          message: 'Firearm Caliber is required',
        },
      ],
      firearmActionId: [
        {
          required:
            formData.licenceTypeId ===
            EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM ||
            formData.memberFirearmId ===
            EndorsementFirearmRenewalConstants.NEW_FIREARM,
          message: 'Firearm Action is required',
        },
      ],
      firearmSerialTypeId: [
        {
          required: true,
          message: 'Firearm Serial Type is required',
        },
      ],
      frameSerial: [
        {
          required:
            formData.firearm?.firearmSerialTypeId ===
            FirearmSerialTypeConstants.RECEIVER_FRAME_SERIAL_NUMBER ||
            formData.firearm?.firearmSerialTypeId ===
            FirearmSerialTypeConstants.BARREL_AND_RECEIVER_FRAME_SERIAL_NUMBER,
          message: 'Frame Serial is required',
        },
      ],
      barrelSerial: [
        {
          required:
            formData.firearm?.firearmSerialTypeId ===
            FirearmSerialTypeConstants.BARREL_SERIAL_NUMBER ||
            formData.firearm?.firearmSerialTypeId ===
            FirearmSerialTypeConstants.BARREL_AND_RECEIVER_FRAME_SERIAL_NUMBER,
          message: 'Barrel Serial is required',
        },
      ],
      firearmBarrelId: [
        {
          required:
            formData.licenceTypeId ===
            EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM,
          message: 'Firearm Barrel is required',
        },
      ],
      firearmPurposeId: [
        {
          required: true,
          message: 'Firearm Purpose is required',
        },
      ],
    },
    motivationTypeId: [
      { required: true, message: 'Motivation Type is required' },
    ],
    fellowId: [
      { required: formData.motivationTypeId == FirearmMotivationTypeConstants.SOMEONE_ELSE, message: 'Fellow is required' },
    ],
    motivationAuthor: [
      {
        required:
          formData.fellowId ===
          FellowConstants.NOT_ON_LIST,
        message: 'Motivation Author is required',
      },
    ],
    motivationFile1Id: [
      { required: true, message: 'Motivation File1 is required' },
    ],
  };

  const { errors, validate } =
    useFormValidation<MemberEndorsementDTO>(validationRules);

  useEffect(() => {
    setLicenceTypes(licenceTypesData || []);
  }, [licenceTypesData]);

  useEffect(() => {
    setMotivationTypes(motivationTypesData || []);
  }, [motivationTypesData]);

  useEffect(() => {
    setStatuses(statusesData || []);
  }, [statusesData]);

  const fetchMember = async () => {
    try {
      const response = await getMemberById(memberId ?? '');
      setMember(response?.data);
    } catch (error) {
      console.error('Error fetching Member', error);
    }
  };

  const fetchMemberFirearms = async () => {
    try {
      const response = await searchMemberFirearms({
        filters: {
          memberId: formData.memberId,
        },
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      setMemberFirearms(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching member firearms:', error);
    }
  };

  useEffect(() => {
    fetchMember();
    fetchMemberFirearms();
  }, []);

  // Load audit logs when page changes
  useEffect(() => {
    const fetchAuditLogs = async () => {
      if (initialValues.id) {
        try {
          const response = await searchAuditLogs({
            page: auditCurrentPage,
            pageSize: auditPageSize,
            sortBy: 'CreatedUtc',
            sortDescending: true,
            filters: {
              tableName: 'MemberEndorsement',
              recordId: initialValues.id,
            },
          });

          if (response && response?.success && response?.data) {
            setAuditLogs(response.data.results as AuditLogDTO[]);
            setAuditTotalPages(
              Math.ceil((response.data.totalCount ?? 0) / auditPageSize),
            );
          }
        } catch (error) {
          console.error('Error fetching audit logs:', error);
        }
      }
    };

    fetchAuditLogs();
  }, [initialValues.id, auditCurrentPage, auditPageSize]);

  // Update the handleFileChange function:
  const handleFileChange =
    (field: keyof MemberEndorsementDTO) =>
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
          try {
            setUploadingFiles((prev) => ({ ...prev, [field]: true }));
            const response = await uploadFile(
              'files',
              file.name,
              file,
              'endorsements',
            );
            if (response && response.success && response.data) {
              setFormData((prev) => ({
                ...prev,
                [field]: response.data,
              }));
            }
          } catch (error) {
            console.error('Error handling file:', error);
          } finally {
            setUploadingFiles((prev) => ({ ...prev, [field]: false }));
          }
        }
      };

  useEffect(() => {
    // Only set isEndorsementFirearm on create (when id is empty)
    if (formData.id === '') {
      setFormData((prev) => ({
        ...prev,
        firearm: {
          ...prev.firearm,
          isEndorsementFirearm: !saveFireArm.saveFireArm,
        },
      }));
    }
  }, [saveFireArm.saveFireArm]);

  const handleSave = async () => {
    if (!validate(formData)) return;

    if (
      roles?.includes('User') &&
      formData.licenceTypeId === EndorsementLicenceTypeConstants.RENEWAL_OF_EXISTING_LICENCE
    ) {
      const confirmed = await confirmAction({
        title: 'Confirm Declaration',
        text: `I, ${member?.firstName} ${member?.lastName} (${member?.idPassportNumber}), agree that the information is correct and that my application will create a declaration document that can be displayed on my application.`,
        icon: 'warning',
        confirmButtonText: 'Yes, I confirm',
        cancelButtonText: 'Cancel',
      });
      if (!confirmed) return;
    }

    onSubmit(formData);
  };

  const handleMotivationTypeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    handleChange(e, setFormData, () => {
      setFormData((prev) => ({
        ...prev,
        fellowId: undefined,
        motivationAuthor: undefined,
      }));
    });
  };

  const handleFellowOther = () => {
    setFormData((prev) => ({
      ...prev,
      fellowId: FellowConstants.NOT_ON_LIST,
    }));
  };

  const sections = Object.values(FORM_SECTIONS);
  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      {roles?.includes('Admin') && FORM_SECTIONS.memberDetails && (
        <FormSection
          id={FORM_SECTIONS.memberDetails.id}
          title={FORM_SECTIONS.memberDetails.title}
        >
          <MemberDetails member={formData.member ?? {}} />
        </FormSection>
      )}
      <FormSection
        id={FORM_SECTIONS.firearmDetails.id}
        title={FORM_SECTIONS.firearmDetails.title}
      >
        <FormField
          id="licenceTypeId"
          label="Application Type"
          type="dropdown"
          value={formData.licenceTypeId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Application Type"
          required={true}
          error={errors.licenceTypeId}
          options={licenceTypes.map((type) => ({
            value: type.id,
            label: type.title,
          }))}
          isLoading={isLoadingLicenceTypes}
        />
        {formData.licenceTypeId ===
          EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM && (
            <>
              <FirearmFieldsForm
                formData={formData.firearm || {}}
                setFormData={setFirearmData}
                licenceTypeId={formData.licenceTypeId}
                memberFirearmId={formData.memberFirearmId}
                errors={errors}
              />
              <FormField
                id="isAdditional"
                label="Additional firearm licence holder?"
                type="checkbox"
                value={formData.isAdditional || ''}
                onChange={(e) => handleChange(e, setFormData)}
                placeholder="Enter Is Additional"
                required={false}
                error={errors.isAdditional}
              />
            </>
          )}

        {formData.licenceTypeId ===
          EndorsementLicenceTypeConstants.RENEWAL_OF_EXISTING_LICENCE && (
            <>
              <FormField
                id="memberFirearmId"
                label="Firearm"
                type="dropdown"
                value={formData.memberFirearmId || ''}
                onChange={(e) => handleChange(e, setFormData)}
                placeholder="Select Your Firearm"
                required={
                  formData.licenceTypeId ===
                  EndorsementLicenceTypeConstants.RENEWAL_OF_EXISTING_LICENCE
                }
                error={errors.memberFirearmId}
                helper={
                  <span className="flex flex-col">
                    <span>
                      Only complete and active firearms will display in the list
                      and where you are the owner.
                    </span>
                    <span>
                      Not on list?{' '}
                      <a
                        className="cursor-pointer text-primary"
                        onClick={() => navigate('/admin/memberfirearms')}
                      >
                        manage
                      </a>
                      <span> or </span>
                      <a
                        className="cursor-pointer text-primary"
                        onClick={() => navigate('/admin/memberfirearms/create')}
                      >
                        add a new firearm?
                      </a>
                    </span>
                  </span>
                }
                options={[
                  {
                    value: EndorsementFirearmRenewalConstants.NEW_FIREARM,
                    label: '+ Add New Firearm',
                    searchText: 'Add New Firearm',
                  },
                  ...buildFirearmOptions(memberFirearms),
                ]}
                isLoading={isLoadingMemberFirearms}
              />
              <FirearmFieldsForm
                formData={formData.firearm || {}}
                setFormData={setFirearmData}
                licenceTypeId={formData.licenceTypeId}
                memberFirearmId={formData.memberFirearmId}
                errors={errors}
              />
            </>
          )}

        {formData.id === '' &&
          (formData.licenceTypeId ===
            EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM ||
            formData.memberFirearmId ===
            EndorsementFirearmRenewalConstants.NEW_FIREARM) && (
            <FormField
              id="saveFireArm"
              label="Save new firearm?"
              type="checkbox"
              value={saveFireArm.saveFireArm}
              onChange={(e) => handleChange(e, setSaveFireArm)}
              required={false}
              helper="This option will add your new firearm under My Firearms for future use and to load scores and activities."
            />
          )}
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.endorsementDetails.id}
        title={FORM_SECTIONS.endorsementDetails.title}
        columns={1}
      >
        <FormField
          id="motivationTypeId"
          label="Who wrote your motivation?"
          type="dropdown"
          value={formData.motivationTypeId || ''}
          onChange={handleMotivationTypeChange}
          placeholder="Select Motivation Type"
          required={true}
          error={errors.motivationTypeId}
          options={motivationTypes.map((type) => ({
            value: type.id,
            label: type.title,
          }))}
          isLoading={isLoadingMotivationTypes}
        />
        {formData.motivationTypeId ===
          FirearmMotivationTypeConstants.SOMEONE_ELSE && (
            <FormField
              id="fellowId"
              label="Motivation Author"
              type="dropdown"
              value={formData.fellowId || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Select Motivation Author"
              required={formData.motivationTypeId == FirearmMotivationTypeConstants.SOMEONE_ELSE}
              error={errors.fellowId}
              options={buildFellowOptions(fellows || [])}
              isLoading={isLoadingFellows}
              helper={
                <span>
                  Not on list?{' '}
                  <a
                    className="cursor-pointer text-primary"
                    onClick={handleFellowOther}
                  >
                    Click here
                  </a>
                </span>
              }
            />
          )}
        {formData.motivationTypeId === FirearmMotivationTypeConstants.SOMEONE_ELSE &&
          formData.fellowId === FellowConstants.NOT_ON_LIST && (

            <FormField
              id="motivationAuthor"
              label="Name of Motivation Author"
              type="text"
              value={formData.motivationAuthor || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Motivation Author"
              required={
                formData.fellowId === FellowConstants.NOT_ON_LIST
              }
              error={errors.motivationAuthor}
            />
          )}

        {formData.motivationFile1Id && (
          <ImageLink
            fileId={formData.motivationFile1Id}
            label="Motivation File"
          />
        )}
        <FileUpload
          id="motivationFile1Upload"
          onChange={handleFileChange('motivationFile1Id')}
          title="Upload Motivation File"
          accept={FileTypeConstants.ALL_SUPPORTED}
          required={true}
          error={errors.motivationFile1Id}
          isUploading={uploadingFiles['motivationFile1Id']}
        />

        {formData.motivationFile2Id && (
          <ImageLink
            fileId={formData.motivationFile2Id}
            label="Motivation Additional File 1"
          />
        )}
        <FileUpload
          id="motivationFile2Upload"
          onChange={handleFileChange('motivationFile2Id')}
          title="Upload Motivation Additional File 1"
          accept={FileTypeConstants.ALL_SUPPORTED}
          required={false}
          isUploading={uploadingFiles['motivationFile2Id']}
        />

        {formData.motivationFile3Id && (
          <ImageLink
            fileId={formData.motivationFile3Id}
            label="Motivation Additional File 2"
          />
        )}
        <FileUpload
          id="motivationFile3Upload"
          onChange={handleFileChange('motivationFile3Id')}
          title="Upload Motivation Additional File 2"
          accept={FileTypeConstants.ALL_SUPPORTED}
          required={false}
          isUploading={uploadingFiles['motivationFile3Id']}
        />

        {formData.motivationFile4Id && (
          <ImageLink
            fileId={formData.motivationFile4Id}
            label="Motivation Additional File 3"
          />
        )}
        <FileUpload
          id="motivationFile4Upload"
          onChange={handleFileChange('motivationFile4Id')}
          title="Upload Motivation Additional File 3"
          accept={FileTypeConstants.ALL_SUPPORTED}
          required={false}
          isUploading={uploadingFiles['motivationFile4Id']}
        />
      </FormSection>

      {roles?.includes('Admin') && (
        <FormSection
          id={FORM_SECTIONS.feedback.id}
          title={FORM_SECTIONS.feedback.title}
        >
          <FeedbackForm
            componentId={ComponentConstants.ENDORSEMENTS}
            recordId={formData.id ?? ''}
            initialData={formData}
            componentStatuses={statuses.map((status) => ({
              value: status.id,
              label: status.title,
            }))}
            statusFieldName={statusFieldName}
            onChange={(updatedData) => {
              setFormData((prev) => ({
                ...prev,
                ...updatedData,
              }));
            }}
          />
        </FormSection>
      )}
      {roles?.includes('Admin') && FORM_SECTIONS.history && (
        <FormSection
          id={FORM_SECTIONS.history.id}
          title={FORM_SECTIONS.history.title}
        >
          <AuditLogTable
            data={auditLogs}
            currentPage={auditCurrentPage}
            totalPages={auditTotalPages}
            setCurrentPage={setAuditCurrentPage}
            pageSize={auditPageSize}
            setPageSize={setAuditPageSize}
            isLoading={isLoadingAuditLogs}
          />
        </FormSection>
      )}
    </Form>
  );
};

export default MemberEndorsementForm;
